import { Container, IDestroyOptions, Sprite, Texture } from "pixi.js"
import { TileMap, TileSize } from "game-common/models"
import { contentTexture } from "./content_textures"

/**
 * RAISED GROUND, DRAWN: the step along the edge of the room's `rise` layer (common/room/ember/forest_rise.ts
 * decides where the ground is raised; this only draws it).
 *
 * The step is drawn as a 3/4 view sees one, at the boundary, with nothing moved -- the ember grass is one
 * even texture, so lifting a raised patch a few pixels would look exactly like leaving it in place:
 *  - SOUTH (the drop faces the camera): a solid face on the raised side, FACE_HEIGHT px tall, darker and
 *    earthier toward its foot, with grass strands down it; the raised grass hangs ragged over its top,
 *    a lit lip above that, the lower ground's grass growing up in front of its foot, and a contact
 *    shadow on the ground below.
 *  - NORTH (the far rim): it faces the sun (upper right, see sun.ts), so it is lit, and the raised
 *    grass's blades poke up over the ground behind. A dark rim here reads as a circle drawn on the grass.
 *  - WEST / EAST: blades spill a pixel or two over the side; the west side is shaded, the east one lit.
 *
 * Only pixels the step changes are baked, everything else stays transparent, so the ground layer still
 * shows through and nothing about the raised patch's interior is copied. Baked once per room, in
 * CHUNK-sized pieces a few per frame (a budget machine should never hitch on it), between map1 and map2:
 * flowers, dirt edges and building floors all draw over it. Colours are read from the ground tiles'
 * own atlas pixels, the way tree_root_fringe reads its roots, so the face is made of the grass it is cut into.
 *
 * Live tuning: window.groundRise.enabled / .faceHeight, or window.groundRise = false.
 */
export const GROUND_RISE = { enabled: true, faceHeight: 6 }

export const groundRiseConfig = (): typeof GROUND_RISE => {
    if (typeof window === "undefined") return GROUND_RISE
    const w = window as any
    if (w.groundRise === undefined) w.groundRise = GROUND_RISE
    const live = w.groundRise
    if (live === false) return { ...GROUND_RISE, enabled: false }
    return live && typeof live === "object" ? live : GROUND_RISE
}

/** Pixels per side of one baked piece (4x4 tiles). */
export const RISE_CHUNK = 128
/** Ground read round each piece: everything the step does reaches less than this far. */
export const RISE_MARGIN = 12
/** Most frame time the bake may take before it waits for the next frame. */
const BAKE_BUDGET_MS = 4

const hash = (x: number, y: number, seed: number): number => {
    let h = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ seed
    h = Math.imul(h ^ h >>> 13, 1274126177)
    return ((h ^ h >>> 16) >>> 0) / 4294967295
}
const smooth = (v: number) => v * v * (3 - 2 * v)
const valueNoise = (x: number, y: number, seed: number): number => {
    const ix = Math.floor(x), iy = Math.floor(y)
    const sx = smooth(x - ix), sy = smooth(y - iy)
    const a = hash(ix, iy, seed), b = hash(ix + 1, iy, seed), c = hash(ix, iy + 1, seed), d = hash(ix + 1, iy + 1, seed)
    const top = a + (b - a) * sx
    return top + ((c + (d - c) * sx) - top) * sy
}

/** True where cell (x, y) is raised; anything off the map is not. */
export type RiseCells = (x: number, y: number) => boolean

/**
 * 1 per world pixel of the region (x0, y0, w, h) that is raised ground. Each cell corner carries the share
 * of the four cells round it that are raised, interpolated smoothly across the cell and cut at one half,
 * so a straight run sits on the cell line and a patch's corners round off; a gentle wobble (long, not
 * fine -- fine noise zigzags the edge into slivers) keeps it from looking ruled.
 */
export const riseMask = (cells: RiseCells, x0: number, y0: number, w: number, h: number): Uint8Array => {
    const mask = new Uint8Array(w * h)
    const share = (vx: number, vy: number) =>
        (+cells(vx - 1, vy - 1) + +cells(vx, vy - 1) + +cells(vx - 1, vy) + +cells(vx, vy)) / 4
    const corners = new Map<number, number>()
    const corner = (vx: number, vy: number) => {
        const key = vx * 65536 + vy
        let v = corners.get(key)
        if (v === undefined) corners.set(key, (v = share(vx, vy)))
        return v
    }
    for (let y = 0; y < h; y++) {
        const py = y0 + y, cy = Math.floor(py / TileSize), fy = smooth(py / TileSize - cy)
        for (let x = 0; x < w; x++) {
            const px = x0 + x, cx = Math.floor(px / TileSize), fx = smooth(px / TileSize - cx)
            const c00 = corner(cx, cy), c10 = corner(cx + 1, cy), c01 = corner(cx, cy + 1), c11 = corner(cx + 1, cy + 1)
            if (c00 === 0 && c10 === 0 && c01 === 0 && c11 === 0) continue
            if (c00 === 1 && c10 === 1 && c01 === 1 && c11 === 1) { mask[y * w + x] = 1; continue }
            const f = (c00 + (c10 - c00) * fx) * (1 - fy) + (c01 + (c11 - c01) * fx) * fy +
                0.30 * (valueNoise(px / 22, py / 22, 71) - 0.5) + 0.05 * (valueNoise(px / 7, py / 7, 72) - 0.5)
            if (f > 0.5) mask[y * w + x] = 1
        }
    }
    return mask
}

const SOIL = [92, 64, 38]
const DEEP = [26, 64, 31]

/**
 * The step's art over one region of ground. `ground` is the region's RGBA, `mask` its riseMask; (x0, y0)
 * is the region's world position, which every per-pixel variation is hashed from, so neighbouring regions
 * join exactly. Returns RGBA for the INTERIOR (the region less `margin` on every side): the step's pixels
 * opaque, every pixel it leaves alone fully transparent.
 */
export const paintRiseEdges = (ground: Uint8ClampedArray, mask: Uint8Array, w: number, h: number,
    x0: number, y0: number, margin: number, faceHeight: number = GROUND_RISE.faceHeight): Uint8ClampedArray => {
    const FH = Math.max(1, Math.round(faceHeight))
    const n = w * h
    const out = new Float32Array(n * 3)
    for (let i = 0; i < n; i++) {
        out[i * 3] = ground[i * 4]; out[i * 3 + 1] = ground[i * 4 + 1]; out[i * 3 + 2] = ground[i * 4 + 2]
    }
    const up = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && mask[y * w + x] === 1
    const lowAt = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && mask[y * w + x] === 0
    const g = (x: number, y: number, c: number) => ground[(Math.min(h - 1, Math.max(0, y)) * w + x) * 4 + c]
    const scale = (i: number, m: number) => { out[i * 3] *= m; out[i * 3 + 1] *= m; out[i * 3 + 2] *= m }
    const colHash = (x: number, k: number, seed: number) => hash(x0 + x, k, seed)

    // s: rows down to the first lower pixel (1 = right above the drop); FH + 2 when there is none in reach.
    const s = new Uint8Array(n)
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        if (!mask[y * w + x]) continue
        let k = 1
        while (k <= FH + 1 && y + k < h && mask[(y + k) * w + x]) k++
        s[y * w + x] = k <= FH + 1 && y + k < h ? k : FH + 2
    }
    const isFace = (i: number) => s[i] > 0 && s[i] <= FH

    // The face, its strands, and the raised grass hanging over its top.
    const hangCap = Math.round(FH * 0.45)
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        if (!isFace(i)) continue
        const depth = FH - s[i] + 1, t = depth / FH, lip = y - depth
        const openSide = lowAt(x - 1, y) || lowAt(x + 1, y)
        const e = Math.min(1, Math.max(0, t - 0.55))
        const strand = depth <= Math.floor(colHash(x, 0, 41) * 7) && colHash(x, 1, 42) > 0.35
        const hang = Math.min(Math.floor(colHash(x, 2, 43) * 3.2), hangCap)
        for (let c = 0; c < 3; c++) {
            const top = g(x, lip, c)
            let v = openSide ? top * (0.84 - 0.10 * t) : (top * (0.66 - 0.22 * t) + DEEP[c] * 0.15) * (1 - 0.35 * e) + SOIL[c] * 0.35 * e
            if (strand) v = top * 0.80
            if (depth <= hang) v = top * (depth === hang ? 0.86 : 0.97)
            out[i * 3 + c] = v
        }
    }
    // The lit lip above the face.
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        if (s[i] === FH + 1 && colHash(x, 3, 44) > 0.25) scale(i, 1.13)
    }
    // The ground below: a contact shadow, and its grass growing up in front of the face's foot.
    const contact = [0.78, 0.86, 0.93, 0.97]
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        if (mask[i]) continue
        for (let k = 1; k <= contact.length; k++) {
            if (y - k < 0) break
            if (mask[(y - k) * w + x]) { scale(i, contact[k - 1]); break }
        }
    }
    const bladeCap = Math.round(FH * 0.35)
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        if (!isFace(i)) continue
        const len = colHash(x, 4, 45) > 0.5 ? Math.floor(colHash(x, 5, 46) * 3.4) : 0
        if (s[i] <= len && s[i] <= bladeCap) for (let c = 0; c < 3; c++) out[i * 3 + c] = g(x, y + s[i] + 2, c) * 0.92
    }
    // The far rim catches the sun; its blades poke up over the ground behind.
    for (let y = 1; y < h; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        if (mask[i] && !mask[i - w] && colHash(x, 9, 50) > 0.2) scale(i, 1.10)
    }
    const tip = new Uint8Array(n)
    for (let y = 0; y < h - 1; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        if (!mask[i] && mask[i + w] && colHash(x, 6, 47) > 0.45) {
            tip[i] = 1
            for (let c = 0; c < 3; c++) out[i * 3 + c] = out[(i + w) * 3 + c] * 0.94
        }
    }
    for (let y = 0; y < h - 2; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        if (!mask[i] && tip[i + w] && colHash(x, 7, 48) > 0.75) {
            for (let c = 0; c < 3; c++) out[i * 3 + c] = out[(i + 2 * w) * 3 + c] * 0.90
        }
    }
    // Sides: blades spill over; the west side falls in shade, the east side catches the light.
    for (const dx of [-1, 1]) {
        const edgeColour = new Float32Array(n * 3)
        const edge = new Uint8Array(n)
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
            const i = y * w + x
            if (mask[i] && lowAt(x + dx, y)) {
                edge[i] = 1
                edgeColour[i * 3] = out[i * 3]; edgeColour[i * 3 + 1] = out[i * 3 + 1]; edgeColour[i * 3 + 2] = out[i * 3 + 2]
            }
        }
        for (let y = 0; y < h; y++) {
            const spill = Math.floor(hash((y0 + y) * 7 + (dx > 0 ? 1 : 0), 8, 49) * 2.6)
            for (let x = 0; x < w; x++) {
                const i = y * w + x
                if (!edge[i]) continue
                for (let k = 1; k <= Math.min(2, spill); k++) {
                    const tx = x + dx * k
                    if (!lowAt(tx, y)) continue
                    const j = y * w + tx
                    for (let c = 0; c < 3; c++) out[j * 3 + c] = edgeColour[i * 3 + c] * (0.96 - 0.06 * k)
                }
                if (dx < 0) {
                    for (const [k, m] of [[1, 0.86], [2, 0.93]]) {
                        const tx = x - (spill + k)
                        if (lowAt(tx, y)) scale(y * w + tx, m)
                    }
                } else {
                    scale(i, 1.07)
                }
            }
        }
    }

    const iw = w - 2 * margin, ih = h - 2 * margin
    const rgba = new Uint8ClampedArray(Math.max(0, iw * ih * 4))
    for (let y = 0; y < ih; y++) for (let x = 0; x < iw; x++) {
        const i = (y + margin) * w + x + margin, o = (y * iw + x) * 4
        const r = Math.round(Math.min(255, Math.max(0, out[i * 3])))
        const gg = Math.round(Math.min(255, Math.max(0, out[i * 3 + 1])))
        const b = Math.round(Math.min(255, Math.max(0, out[i * 3 + 2])))
        if (r === ground[i * 4] && gg === ground[i * 4 + 1] && b === ground[i * 4 + 2]) continue
        rgba[o] = r; rgba[o + 1] = gg; rgba[o + 2] = b; rgba[o + 3] = 255
    }
    return rgba
}

/** Cells whose 3x3 neighbourhood holds both raised and flat ground: the only ones a step can touch. */
export const riseEdgeCells = (cells: RiseCells, width: number, height: number): Array<{ x: number; y: number }> => {
    const out: Array<{ x: number; y: number }> = []
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        const here = cells(x, y)
        let differs = false
        for (let dy = -1; dy <= 1 && !differs; dy++) for (let dx = -1; dx <= 1 && !differs; dx++) {
            if (cells(x + dx, y + dy) !== here) differs = true
        }
        if (differs) out.push({ x, y })
    }
    return out
}

/** What the layer needs from a room's tile map. */
export interface RiseMapSource {
    roomId?: string
    width(): number
    height(): number
    getRiseLayer?(): TileMap | undefined
    resolveTileMapFromLayerId(layerId: string): TileMap
    resolveTilesetTileId(tile: string): string
}

interface RiseChunk {
    key: string
    x: number
    y: number
    /** What the sprite was baked from; a piece is re-baked whenever what it would be baked from differs. */
    baked: string
    sprite?: Sprite
}

const cellsOf = (rise: TileMap | undefined, width: number, height: number): RiseCells =>
    (x, y) => !!rise && x >= 0 && y >= 0 && x < width && y < height && rise[x]?.[y] === "1"

/** The ground the step is cut into, drawn from map1's own atlas pixels; undefined until every atlas is decoded. */
export type GroundReader = (source: RiseMapSource, px: number, py: number, w: number, h: number) => Uint8ClampedArray | undefined

export const readAtlasGround: GroundReader = (source: RiseMapSource, px: number, py: number, w: number, h: number): Uint8ClampedArray | undefined => {
    if (typeof document === "undefined") return undefined
    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    const context = canvas.getContext("2d")
    if (!context) return undefined
    const ground = source.resolveTileMapFromLayerId("map1")
    for (let cy = Math.floor(py / TileSize); cy * TileSize < py + h; cy++) {
        for (let cx = Math.floor(px / TileSize); cx * TileSize < px + w; cx++) {
            const tile = ground?.[cx]?.[cy]
            if (!tile || tile === "0") continue
            const texture = contentTexture(source.resolveTilesetTileId(tile))
            const image = (texture?.baseTexture?.resource as any)?.source
            if (!image || !texture.baseTexture.valid) return undefined
            const frame = texture.frame
            context.drawImage(image, frame.x, frame.y, frame.width, frame.height,
                cx * TileSize - px, cy * TileSize - py, TileSize, TileSize)
        }
    }
    try {
        return context.getImageData(0, 0, w, h).data
    } catch {
        return undefined // a tainted canvas: draw no step rather than fail the map
    }
}

/**
 * The room's baked step, one sprite per CHUNK that the boundary crosses. sync() after every full map
 * render works out which pieces exist and which changed (rise layer, the ground under them, the config);
 * tick() bakes the waiting ones within the frame budget.
 */
export class GroundRiseLayer extends Container {
    private source?: RiseMapSource
    private chunks = new Map<string, RiseChunk>()
    private queue: Array<{ chunk: RiseChunk; signature: string }> = []
    private configKey = ""

    constructor(private readGround: GroundReader = readAtlasGround) {
        super()
        this.interactiveChildren = false
    }

    sync = (source: RiseMapSource | undefined): void => {
        const config = groundRiseConfig()
        this.configKey = `${config.enabled}|${config.faceHeight}`
        const rise = source?.getRiseLayer?.()
        if (!source || !rise || !config.enabled) {
            this.source = source
            this.clearChunks()
            this.visible = false
            return
        }
        if (source.roomId !== this.source?.roomId) this.clearChunks()
        this.source = source
        this.visible = true
        const width = source.width(), height = source.height()
        const cells = cellsOf(rise, width, height)
        const ground = source.resolveTileMapFromLayerId("map1")
        const per = RISE_CHUNK / TileSize
        const wanted = new Map<string, { x: number; y: number }>()
        for (const cell of riseEdgeCells(cells, width, height)) {
            const x = Math.floor(cell.x / per), y = Math.floor(cell.y / per)
            wanted.set(`${x},${y}`, { x, y })
        }
        this.chunks.forEach((chunk, key) => { if (!wanted.has(key)) this.dropChunk(chunk) })
        const reach = Math.ceil(RISE_MARGIN / TileSize)
        this.queue = []
        wanted.forEach(({ x, y }, key) => {
            // Everything a piece is baked from: the raised cells and the ground tiles in reach of it.
            const parts = [this.configKey]
            for (let cy = y * per - reach; cy < (y + 1) * per + reach; cy++) {
                for (let cx = x * per - reach; cx < (x + 1) * per + reach; cx++) {
                    parts.push(`${+cells(cx, cy)}${ground?.[cx]?.[cy] ?? ""}`)
                }
            }
            const signature = parts.join(",")
            const chunk = this.chunks.get(key) ?? { key, x, y, baked: "" }
            this.chunks.set(key, chunk)
            if (chunk.baked !== signature) this.queue.push({ chunk, signature })
        })
    }

    /** Bake waiting pieces until the frame budget runs out. */
    tick = (budgetMs: number = BAKE_BUDGET_MS): void => {
        if (!this.source) return
        const config = groundRiseConfig()
        if (`${config.enabled}|${config.faceHeight}` !== this.configKey) this.sync(this.source)
        if (!this.queue.length) return
        const start = performance.now()
        const rise = this.source.getRiseLayer?.()
        const width = this.source.width(), height = this.source.height()
        const cells = cellsOf(rise, width, height)
        while (this.queue.length && performance.now() - start < budgetMs) {
            const { chunk, signature } = this.queue[0]
            const px = chunk.x * RISE_CHUNK, py = chunk.y * RISE_CHUNK
            // The piece and its margin, kept inside the map (there is no ground to read past its edge).
            const rx = Math.max(0, px - RISE_MARGIN), ry = Math.max(0, py - RISE_MARGIN)
            const rw = Math.min(width * TileSize, px + RISE_CHUNK + RISE_MARGIN) - rx
            const rh = Math.min(height * TileSize, py + RISE_CHUNK + RISE_MARGIN) - ry
            const groundPixels = this.readGround(this.source, rx, ry, rw, rh)
            if (!groundPixels) return // an atlas still decoding: try again next frame, never mark it done
            this.queue.shift()
            // Pad to a full margin on every side so the painter's interior is exactly this piece.
            const left = px - rx, top = py - ry
            const pw = rw + (RISE_MARGIN - left) + Math.max(0, px + RISE_CHUNK + RISE_MARGIN - (rx + rw))
            const ph = rh + (RISE_MARGIN - top) + Math.max(0, py + RISE_CHUNK + RISE_MARGIN - (ry + rh))
            const padded = new Uint8ClampedArray(pw * ph * 4)
            for (let y = 0; y < rh; y++) {
                padded.set(groundPixels.subarray(y * rw * 4, (y + 1) * rw * 4), ((y + RISE_MARGIN - top) * pw + RISE_MARGIN - left) * 4)
            }
            const ox = px - RISE_MARGIN, oy = py - RISE_MARGIN
            const mask = riseMask(cells, ox, oy, pw, ph)
            const rgba = paintRiseEdges(padded, mask, pw, ph, ox, oy, RISE_MARGIN, config.faceHeight)
            this.placeChunk(chunk, rgba)
            chunk.baked = signature
        }
    }

    private placeChunk = (chunk: RiseChunk, rgba: Uint8ClampedArray): void => {
        if (chunk.sprite) {
            chunk.sprite.destroy({ texture: true, baseTexture: true })
            chunk.sprite = undefined
        }
        if (!rgba.some((v, i) => i % 4 === 3 && v > 0)) return
        const canvas = document.createElement("canvas")
        canvas.width = RISE_CHUNK
        canvas.height = RISE_CHUNK
        const context = canvas.getContext("2d")
        if (!context) return
        context.putImageData(new ImageData(rgba, RISE_CHUNK, RISE_CHUNK), 0, 0)
        const sprite = new Sprite(Texture.from(canvas))
        sprite.position.set(chunk.x * RISE_CHUNK, chunk.y * RISE_CHUNK)
        chunk.sprite = sprite
        this.addChild(sprite)
    }

    private dropChunk = (chunk: RiseChunk): void => {
        chunk.sprite?.destroy({ texture: true, baseTexture: true })
        this.chunks.delete(chunk.key)
    }

    private clearChunks = (): void => {
        this.chunks.forEach(chunk => chunk.sprite?.destroy({ texture: true, baseTexture: true }))
        this.chunks.clear()
        this.queue = []
    }

    /** True while pieces are still waiting to bake. */
    get baking(): boolean {
        return this.queue.length > 0
    }

    destroy(options?: IDestroyOptions | boolean): void {
        this.clearChunks()
        super.destroy(options)
    }
}
